import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  CalendarClock,
  Check,
  ArrowUpRight,
  ArrowLeft,
  Globe,
  Video,
  Share2,
  Megaphone,
  Star,
  Quote,
  Smartphone,
  Gift,
  MapPin,
  Bell,
  Stamp,
  Zap,
  Loader2,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { LocaleProvider, useLocale } from "@/i18n/LocaleContext";
import useLenis from "@/hooks/useLenis";
import { MaskText, FadeUp } from "./MaskText";
import { smeContent, smePath, PRICING } from "@/i18n/sme";
import { WHATSAPP, CALENDLY, WORK, localePath } from "@/i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFER_ICONS = [Globe, Video, Share2, Megaphone];
const APP_ICONS = [Stamp, MapPin, Bell];
const APP_IMAGES = [
  "https://images.unsplash.com/photo-1610444865758-24ca33cd36c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1695561324569-5e47c76dc0a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.pexels.com/photos/5077054/pexels-photo-5077054.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1000",
];
const OFFER_END = new Date("2026-09-09T23:59:59+02:00").getTime();
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5bab2fa5-035e-4f5d-bc49-70e7462c2b4a/images/bccef69b9217e65d3f02fa0ab2471e58cdb8ce29b0c14d2306028a5cf3a28c17.jpeg";

const Countdown = ({ label }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, OFFER_END - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-nowi-bg" data-testid="sme-countdown">
      <span className="opacity-70">{label}</span>
      <span className="font-bold tabular-nums">{d}d : {pad(h)}h : {pad(m)}m : {pad(sec)}s</span>
    </div>
  );
};

const SmeBody = ({ locale, s }) => {
  const { t } = useLocale();
  const home = localePath[locale] || "/";
  const waHref = `${WHATSAPP.url}?text=${encodeURIComponent(s.waPrefill)}`;
  const calendlyUrl = `${CALENDLY.url}?hide_gdpr_banner=1&background_color=050505&text_color=f4f4f0&primary_color=00f0ff`;

  const [formOpen, setFormOpen] = useState(false);
  const [selection, setSelection] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const openForm = (sel) => {
    setSelection(sel);
    setForm({ name: "", email: "", company: "", message: "" });
    setFormOpen(true);
  };
  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t.contact.toastValidation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company,
        interest: `Small Business — ${selection}`,
        message: form.message || `Enquiry about: ${selection}`,
      });
      toast.success(t.contact.toastSuccess);
      setFormOpen(false);
    } catch (err) {
      toast.error(t.contact.toastError);
    } finally {
      setLoading(false);
    }
  };
  const fieldCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-nowi-text placeholder:text-white/30 focus:border-nowi-tech focus:outline-none transition-colors font-manrope";

  return (
    <div className="App bg-nowi-bg text-nowi-text font-manrope min-h-screen">
      <div className="noise-overlay" />

      {/* Slim top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/10" data-testid="sme-header">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to={home} className="font-syncopate font-bold text-lg tracking-[0.15em] text-nowi-text" data-testid="sme-logo-link">
            NOWI<span className="text-nowi-tech">.</span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sme-header-whatsapp"
              className="hidden sm:inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs font-mono uppercase tracking-[0.15em] text-nowi-text hover:border-[#25D366] hover:text-[#25D366] transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> {s.ctaWhats}
            </a>
            <a
              href="#book"
              data-testid="sme-header-book"
              className="inline-flex items-center gap-2 bg-nowi-venture text-nowi-bg px-5 py-2.5 text-xs font-mono uppercase tracking-[0.15em] hover:bg-nowi-text transition-colors"
            >
              {s.ctaBook} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-white/10" data-testid="sme-hero">
          <div className="absolute inset-0" data-testid="sme-hero-bg" aria-hidden="true">
            <img src={HERO_BG} alt="" className="w-full h-full object-cover object-right opacity-70 animate-[heroZoom_26s_ease-in-out_infinite]" />
            <div className="absolute inset-0 bg-gradient-to-r from-nowi-bg via-nowi-bg/85 to-nowi-bg/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-nowi-bg via-transparent to-nowi-bg/50" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32">
            <FadeUp>
              <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-nowi-venture">
                <span className="w-8 h-px bg-nowi-venture" /> {s.badge}
              </span>
            </FadeUp>
            <MaskText
              lines={s.h1}
              className="mt-6 font-playfair text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]"
            />
            <FadeUp delay={0.15}>
              <p className="mt-8 max-w-xl text-nowi-muted text-base md:text-lg">{s.sub}</p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <motion.a
                  href="#book"
                  data-testid="sme-hero-book"
                  animate={{ boxShadow: ["0 0 0px rgba(212,255,0,0)", "0 0 36px rgba(212,255,0,0.6)", "0 0 0px rgba(212,255,0,0)"] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative inline-flex items-center gap-3 bg-nowi-venture text-nowi-bg px-9 py-5 text-sm font-mono uppercase tracking-[0.15em] font-bold hover:bg-nowi-text transition-colors"
                >
                  <CalendarClock className="w-5 h-5" strokeWidth={2} /> {s.ctaBook}
                  <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={2} />
                </motion.a>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sme-hero-whatsapp"
                  className="inline-flex items-center gap-3 border border-white/25 px-8 py-5 text-sm font-mono uppercase tracking-[0.15em] text-nowi-text hover:border-[#25D366] hover:text-[#25D366] transition-colors"
                >
                  <MessageCircle className="w-5 h-5" strokeWidth={1.5} /> {s.ctaWhats}
                </a>
              </div>
            </FadeUp>
            <FadeUp delay={0.35}>
              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.15em] text-nowi-muted">
                {s.trust.map((tr, i) => (
                  <span
                    key={i}
                    data-testid={`sme-trust-${i}`}
                    className={`inline-flex items-center gap-2 ${i === 2 ? "text-nowi-venture font-bold" : ""}`}
                  >
                    {i === 2 ? (
                      <Zap className="w-3.5 h-3.5 text-nowi-venture" strokeWidth={2.5} />
                    ) : (
                      <Check className="w-3.5 h-3.5 text-nowi-venture" strokeWidth={2} />
                    )}{" "}
                    {tr}
                  </span>
                ))}
              </div>
              <p className="mt-4 font-mono text-[10px] tracking-[0.05em] text-nowi-muted/70" data-testid="sme-cancel-note">{s.cancelNote}</p>
            </FadeUp>
          </div>
        </section>

        {/* Anniversary discount */}
        <section className="border-b border-white/10 bg-nowi-venture" data-testid="sme-anniversary">
          <div className="max-w-7xl mx-auto px-6 py-10 md:py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start md:items-center gap-5">
              <Gift className="w-9 h-9 text-nowi-bg shrink-0" strokeWidth={1.5} />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-bg/70 mb-1">{s.anniversary.tag}</div>
                <h3 className="font-playfair text-2xl md:text-3xl text-nowi-bg leading-tight">{s.anniversary.title}</h3>
                <p className="mt-2 max-w-2xl text-nowi-bg/80 text-sm">{s.anniversary.text}</p>
                <Countdown label={s.anniversary.ends} />
              </div>
            </div>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sme-anniversary-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-nowi-bg text-nowi-text px-7 py-4 text-xs font-mono uppercase tracking-[0.15em] hover:bg-black transition-colors"
            >
              {s.anniversary.cta} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </section>

        {/* Why */}
        <section className="border-b border-white/10 py-24 md:py-32" data-testid="sme-why">
          <div className="max-w-7xl mx-auto px-6">
            <MaskText lines={[s.whyTitle]} className="font-playfair text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em] mb-16" />
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {s.why.map((w, i) => (
                <FadeUp key={i} delay={i * 0.08} className="bg-nowi-bg">
                  <div className="p-8 md:p-10 h-full" data-testid={`sme-why-${i}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-venture mb-6">0{i + 1}</div>
                    <h3 className="font-playfair text-2xl md:text-3xl mb-4">{w.t}</h3>
                    <p className="text-nowi-muted text-sm md:text-base">{w.d}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Offer */}
        <section className="border-b border-white/10 py-24 md:py-32" data-testid="sme-offer">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16 max-w-2xl">
              <MaskText lines={[s.offerTitle]} className="font-playfair text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em]" />
              <FadeUp delay={0.1}>
                <p className="mt-5 text-nowi-muted text-sm md:text-base">{s.offerSub}</p>
              </FadeUp>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {s.offers.map((o, i) => {
                const Icon = OFFER_ICONS[i] || Globe;
                return (
                  <FadeUp key={i} delay={i * 0.06} className="bg-nowi-bg">
                    <div className="p-8 h-full flex flex-col" data-testid={`sme-offer-${i}`}>
                      <Icon className="w-8 h-8 text-nowi-tech mb-6" strokeWidth={1.25} />
                      <h3 className="font-playfair text-xl md:text-2xl mb-3">{o.t}</h3>
                      <p className="text-nowi-muted text-sm">{o.d}</p>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mobile apps */}
        <section className="border-b border-white/10 py-24 md:py-32" data-testid="sme-apps">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16 max-w-2xl">
              <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-nowi-tech mb-5">
                <Smartphone className="w-4 h-4" strokeWidth={1.5} /> Apps
              </span>
              <MaskText lines={[s.apps.title]} className="font-playfair text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em]" />
              <FadeUp delay={0.1}>
                <p className="mt-5 text-nowi-muted text-sm md:text-base">{s.apps.sub}</p>
              </FadeUp>
            </div>
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {s.apps.items.map((a, i) => {
                const Icon = APP_ICONS[i] || Smartphone;
                return (
                  <FadeUp key={i} delay={i * 0.08} className="bg-nowi-bg">
                    <div className="h-full flex flex-col" data-testid={`sme-app-${i}`}>
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={APP_IMAGES[i]}
                          alt={a.t}
                          loading="lazy"
                          className="w-full h-full object-cover opacity-70 hover:opacity-90 hover:scale-105 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-nowi-bg via-nowi-bg/40 to-transparent" />
                        <Icon className="absolute bottom-4 left-6 w-8 h-8 text-nowi-tech" strokeWidth={1.5} />
                      </div>
                      <div className="p-8 md:p-10">
                        <h3 className="font-playfair text-xl md:text-2xl mb-3">{a.t}</h3>
                        <p className="text-nowi-muted text-sm">{a.d}</p>
                      </div>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="border-b border-white/10 py-24 md:py-32" data-testid="sme-pricing">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16 max-w-2xl">
              <MaskText lines={[s.pricingTitle]} className="font-playfair text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em]" />
              <FadeUp delay={0.1}>
                <p className="mt-5 text-nowi-muted text-sm md:text-base">{s.pricingSub}</p>
              </FadeUp>
            </div>

            {/* Services with price / on request */}
            <div className="mb-16">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-6">{s.paygLabel}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                {PRICING.services.map((key, i) => {
                  const isWebsite = key === "website";
                  return (
                    <FadeUp key={key} delay={i * 0.05} className="bg-nowi-bg">
                      <div className={`p-8 h-full flex flex-col justify-between ${isWebsite ? "ring-1 ring-nowi-venture/40" : ""}`} data-testid={`sme-service-${key}`}>
                        <div>
                          <h3 className="font-playfair text-xl md:text-2xl mb-2">{s.offers[i].t}</h3>
                          <p className="text-nowi-muted text-sm">{s.offers[i].d}</p>
                        </div>
                        <div className="mt-8">
                          {isWebsite ? (
                            <div className="flex items-baseline gap-2">
                              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-nowi-muted">{s.from}</span>
                              <span className="font-playfair text-3xl text-nowi-venture">{s.websitePrice}</span>
                              <span className="font-mono text-[11px] text-nowi-muted">{s.perMonth}</span>
                            </div>
                          ) : (
                            <span className="font-mono text-xs uppercase tracking-[0.15em] text-nowi-tech">{s.onRequest}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => openForm(s.offers[i].t)}
                            data-testid={`sme-service-cta-${key}`}
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 text-[11px] font-mono uppercase tracking-[0.15em] text-nowi-text hover:border-nowi-venture hover:text-nowi-venture transition-colors"
                          >
                            {s.serviceCta} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    </FadeUp>
                  );
                })}
              </div>
            </div>

            {/* Subscription plans */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-6">{s.subLabel}</div>
              <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {PRICING.plans.map((plan, i) => (
                  <FadeUp key={plan.key} delay={i * 0.08} className="bg-nowi-bg">
                    <div className={`relative p-8 md:p-10 h-full flex flex-col ${plan.popular ? "bg-nowi-surface" : ""}`} data-testid={`sme-plan-${plan.key}`}>
                      {plan.popular && (
                        <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-nowi-bg bg-nowi-venture px-2.5 py-1">
                          <Star className="w-3 h-3" strokeWidth={2} /> {s.popular}
                        </span>
                      )}
                      <h3 className="font-playfair text-3xl mb-2">{s.planNames[i]}</h3>
                      <div className="font-mono text-xs uppercase tracking-[0.15em] text-nowi-tech mb-8">{s.onRequest}</div>
                      <ul className="space-y-4 flex-1">
                        {s.planFeatures[i].map((f, j) => (
                          <li key={j} className="flex items-start gap-3 text-sm text-nowi-text">
                            <Check className="w-4 h-4 mt-0.5 text-nowi-venture shrink-0" strokeWidth={1.5} /> {f}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={() => openForm(s.planNames[i])}
                        data-testid={`sme-plan-cta-${plan.key}`}
                        className={`mt-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
                          plan.popular
                            ? "bg-nowi-venture text-nowi-bg hover:bg-nowi-text"
                            : "border border-white/20 text-nowi-text hover:border-nowi-venture hover:text-nowi-venture"
                        }`}
                      >
                        {s.planCta} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Bernedotter showcase */}
        <section className="border-b border-white/10 py-24 md:py-32" data-testid="sme-work">
          <div className="max-w-7xl mx-auto px-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-venture mb-8">{s.workLabel}</div>
            <a
              href={WORK.bernedotterUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sme-bernedotter"
              className="group relative block overflow-hidden border border-white/10 min-h-[360px] md:min-h-[440px] flex items-end"
            >
              <div className="absolute inset-0">
                <img
                  src={WORK.bernedotterImage}
                  alt="Bernedotter"
                  className="w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-nowi-bg via-nowi-bg/60 to-transparent" />
              </div>
              <div className="relative z-10 p-8 md:p-12">
                <h3 className="font-playfair text-4xl md:text-5xl mb-3">Bernedotter</h3>
                <p className="text-nowi-muted text-sm max-w-md mb-5">Garden care — Öland &amp; Kalmar. A fast, modern site built to win local jobs.</p>
                <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-nowi-text border-b border-nowi-venture pb-1">
                  bernedotter.se
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={1.5} />
                </span>
              </div>
            </a>
          </div>
        </section>

        {/* Testimonials */}
        <section className="border-b border-white/10 py-24 md:py-32" data-testid="sme-testimonials">
          <div className="max-w-7xl mx-auto px-6">
            <MaskText lines={[s.testiTitle]} className="font-playfair text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em] mb-16" />
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {s.testimonials.map((tm, i) => (
                <FadeUp key={i} delay={i * 0.08} className="bg-nowi-bg">
                  <figure className="p-8 md:p-10 h-full flex flex-col justify-between" data-testid={`sme-testimonial-${i}`}>
                    <Quote className="w-8 h-8 text-nowi-venture mb-6" strokeWidth={1.25} />
                    <blockquote className="font-playfair text-xl md:text-2xl leading-snug text-nowi-text">“{tm.q}”</blockquote>
                    <figcaption className="mt-8 font-mono text-[11px] uppercase tracking-[0.15em] text-nowi-muted">
                      {tm.a} · <span className="text-nowi-tech">{tm.c}</span>
                    </figcaption>
                  </figure>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* Dedicated person / WhatsApp */}
        <section className="border-b border-white/10 py-24 md:py-32" data-testid="sme-dedicated">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <MaskText lines={[s.dedTitle]} className="font-playfair text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em]" />
                <FadeUp delay={0.1}>
                  <p className="mt-6 max-w-lg text-nowi-muted text-base md:text-lg">{s.dedSub}</p>
                </FadeUp>
              </div>
              <FadeUp delay={0.15}>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sme-dedicated-whatsapp"
                  className="group flex items-center justify-between gap-4 border border-white/15 p-8 hover:border-[#25D366] transition-colors"
                >
                  <div className="flex items-center gap-5">
                    <MessageCircle className="w-10 h-10 text-[#25D366]" strokeWidth={1.25} />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-muted">{s.dedCta}</div>
                      <div className="font-playfair text-2xl md:text-3xl">+46 728 300 501</div>
                    </div>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-nowi-text group-hover:text-[#25D366] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </a>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <section id="book" className="border-b border-white/10 py-24 md:py-32" data-testid="sme-book">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-14">
              <FadeUp>
                <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-nowi-venture">
                  <CalendarClock className="w-4 h-4" strokeWidth={1.5} /> {s.bookTitle}
                </span>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-5 max-w-md text-nowi-muted text-sm md:text-base">{s.bookSub}</p>
              </FadeUp>
            </div>
            <FadeUp delay={0.15}>
              <iframe
                src={calendlyUrl}
                title="Book a 15-minute call with NOWI"
                data-testid="sme-calendly-widget"
                className="w-full border border-white/10 bg-nowi-surface"
                style={{ minWidth: "320px", height: "720px" }}
                frameBorder="0"
              />
            </FadeUp>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-16" data-testid="sme-footer">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <Link
              to={home}
              data-testid="sme-back-home"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-nowi-muted hover:text-nowi-text transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {s.back}
            </Link>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-muted">
              NOWI Skills AB · Org.nr 559416-0227 · Stockholm
            </div>
          </div>
        </footer>
      </main>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-nowi-bg border border-white/15 text-nowi-text max-w-lg" data-testid="sme-lead-form">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl md:text-3xl text-nowi-text">{s.formTitle}</DialogTitle>
            <DialogDescription className="text-nowi-muted text-sm">{s.formSub}</DialogDescription>
          </DialogHeader>
          <div className="mb-2">
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-nowi-bg bg-nowi-venture px-3 py-1.5" data-testid="sme-form-selection">
              {s.formSelected}: {selection}
            </span>
          </div>
          <form onSubmit={submitForm} className="space-y-5" data-testid="sme-lead-form-el">
            <input
              className={fieldCls}
              placeholder={t.contact.name}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="sme-form-name"
            />
            <input
              type="email"
              className={fieldCls}
              placeholder={t.contact.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="sme-form-email"
            />
            <input
              className={fieldCls}
              placeholder={t.contact.company}
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              data-testid="sme-form-company"
            />
            <textarea
              rows={3}
              className={fieldCls + " resize-none"}
              placeholder={t.contact.message}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="sme-form-message"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="sme-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-nowi-venture text-nowi-bg px-6 py-4 text-xs font-mono uppercase tracking-[0.15em] font-bold hover:bg-nowi-text transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <ArrowUpRight className="w-4 h-4" strokeWidth={2} />}
              {loading ? t.contact.sending : t.contact.send}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const SmallBusiness = ({ locale = "en" }) => {
  useLenis();
  const s = { ...smeContent.en, ...(smeContent[locale] || {}) };
  return (
    <LocaleProvider locale={locale} seoTitle={s.seoTitle} seoDescription={s.seoDescription} canonicalPath={smePath(locale)} ogImage={`/og/smb-${locale}.jpg`}>
      <SmeBody locale={locale} s={s} />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#111111",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 0,
            color: "#F4F4F0",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
    </LocaleProvider>
  );
};

export default SmallBusiness;
